import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PropertyCard } from './PropertyCard';
import type { KanbanProperty } from './types';

interface KanbanColumnProps {
  stage: string;
  title: string;
  color: string;
  properties: KanbanProperty[];
  onCardClick: (property: KanbanProperty) => void;
  isLoading?: boolean;
}

export function KanbanColumn({
  stage,
  title,
  color,
  properties,
  onCardClick,
  isLoading = false,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
  });

  // Stage-specific styling for better visual distinction
  const getStageColorClasses = (stageId: string) => {
    const colorMap: Record<string, string> = {
      lead: 'bg-blue-50 border-blue-200',
      contact: 'bg-yellow-50 border-yellow-200',
      viewing: 'bg-purple-50 border-purple-200',
      offer: 'bg-orange-50 border-orange-200',
      negotiation: 'bg-pink-50 border-pink-200',
      contract: 'bg-green-50 border-green-200',
      closed: 'bg-gray-50 border-gray-200',
    };
    return colorMap[stageId] || 'bg-gray-50 border-gray-200';
  };

  return (
    <div
      ref={setNodeRef}
      className={`
        flex-shrink-0 w-full sm:w-80 lg:w-96 rounded-xl border-2 p-4
        transition-all duration-200 ease-in-out
        ${getStageColorClasses(stage)}
        ${isOver ? 'ring-2 ring-blue-400 ring-opacity-50 scale-[1.02] shadow-lg' : ''}
      `}
      style={{ backgroundColor: color }}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-lg">{title}</h3>
        <span className="text-sm font-medium text-gray-700 bg-white rounded-full px-3 py-1 shadow-sm">
          {properties.length}
        </span>
      </div>

      {/* Cards Container - Scrollable with smooth transitions */}
      <SortableContext items={properties.map(p => p.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 min-h-[200px] max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
          {isLoading ? (
            // Loading skeletons
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-4 shadow-md animate-pulse"
                >
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : properties.length === 0 ? (
            // Empty state with better UX
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3 shadow-sm">
                <span className="text-3xl">📭</span>
              </div>
              <p className="text-gray-500 text-sm font-medium">Engar eignir</p>
              <p className="text-gray-400 text-xs mt-1">
                Dragðu eign hingað til að færa hana
              </p>
            </div>
          ) : (
            // Property cards with smooth transitions
            <div className="space-y-3 transition-all duration-200">
              {properties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onClick={() => onCardClick(property)}
                />
              ))}
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}
